import { ProxyMode } from '@/enums';
import {
  AwsInstanceSize,
  ExecutionSettingsDTO,
  ExecutionSettingsEntity,
} from '@/types';
import {
  convertExecutionSettingsDTOtoEntity,
  convertExecutionSettingsEntityToDTO,
} from '@/utils/convertExecutionSettings';
import {
  Button,
  Grid,
  Icon,
  Input,
  Paper,
  Select,
  Typography,
} from '@prometheus/components';
import {
  SecondaryContentWrapper,
  ConfirmationDialog,
} from '@prometheus/features';
import { DialogContext, UseDialogContext } from '@prometheus/hooks';

import { FC, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';

export interface ExecutionSettingsProps {
  settings: ExecutionSettingsDTO;
  awsInstanceSizes: Array<AwsInstanceSize>;
  update: (data: ExecutionSettingsDTO) => void;
}
export const ExecutionSettingsForm: FC<ExecutionSettingsProps> = ({
  settings,
  awsInstanceSizes,
  update,
}) => {
  const { t } = useTranslation();
  const { showDialog, hideDialog } =
    useContext<UseDialogContext>(DialogContext);

  const [state, setState] = useState<ExecutionSettingsEntity>(
    convertExecutionSettingsDTOtoEntity(settings) || {
      mode: ProxyMode.FIXED,
      fixedUrl: '',
      imageId: '',
      elasticAnalysisTypes: [],
      size: '',
    }
  );

  const handleChange = (key: string, value: any) => {
    setState(prev => ({ ...prev, [key]: value }));
  };
  const handleSave = () => {
    showDialog(ConfirmationDialog, {
      onClose: hideDialog,
      onSubmit: () => {
        update(convertExecutionSettingsEntityToDTO(state));
      },
      text: (
        <>
          You are going to <b>change application configuration properties</b>,
          <br></br>
          do you want to proceed?
        </>
      ),
      confirmButtonText: t('buttons.yes'),
      cancelButtonText: t('buttons.no'),
      variant: 'error',
    });
  };

  return (
    <>
      <Grid item xs={12} container spacing={2} mb={2}>
        <Grid item xs={12} container spacing={1} alignItems="center">
          <Grid item>
            <Typography htmlFor="mode" variant="subtitle2" component="label">
              {t('forms.execution_mode')}
            </Typography>
          </Grid>
          <Grid
            item
            flexGrow={1}
            sx={{
              '.input-root': {
                backgroundColor: 'backgroundColor.dark',
              },
            }}
          >
            <Select
              fullWidth
              value={state.mode}
              className=""
              id="mode"
              placeholder={t('forms.execution_mode_placeholder')}
              options={[
                {
                  name: t('list_controls.fixed_instance'),
                  value: ProxyMode.FIXED,
                },
                {
                  name: t('list_controls.enable_elastic_mode'),
                  value: ProxyMode.ELASTIC,
                },
              ]}
              onChange={value => handleChange('mode', value)}
            />
          </Grid>
        </Grid>
      </Grid>

      <SecondaryContentWrapper>
        <Paper elevation={0} sx={{ p: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} container spacing={2} alignItems="center">
              <Grid item minWidth={200}>
                <Typography
                  htmlFor="fixedUrl"
                  variant="subtitle2"
                  component="label"
                >
                  {t('forms.execution_engine_url')}
                </Typography>
              </Grid>
              <Grid item flexGrow={1}>
                <Input
                  fullWidth
                  value={state.fixedUrl}
                  placeholder={t('forms.execution_engine_url_placeholder')}
                  onChange={e => handleChange('fixedUrl', e.target.value)}
                />
              </Grid>
            </Grid>

            {state.mode === ProxyMode.ELASTIC && (
              <>
                <Grid item xs={12} container spacing={2} alignItems="center">
                  <Grid item minWidth={200}>
                    <Typography
                      htmlFor="imageId"
                      variant="subtitle2"
                      component="label"
                    >
                      {t('forms.image_id')}
                    </Typography>
                  </Grid>
                  <Grid item flexGrow={1}>
                    <Input
                      fullWidth
                      value={state.imageId}
                      placeholder={t('forms.image_id_placeholder')}
                      onChange={e => handleChange('imageId', e.target.value)}
                    />
                  </Grid>
                </Grid>
                <Grid item xs={12} container spacing={2} alignItems="center">
                  <Grid item minWidth={200}>
                    <Typography
                      htmlFor="analysisElasticSupport"
                      variant="subtitle2"
                      component="label"
                    >
                      {t('forms.analysis_elastic_support')}
                    </Typography>
                  </Grid>
                  <Grid item flexGrow={1}>
                    <Select
                      fullWidth
                      value={state.elasticAnalysisTypes
                        .filter(item => item.active)
                        .map(analysis => analysis.value)}
                      multiple
                      placeholder={t(
                        'forms.analysis_elastic_support_placeholder'
                      )}
                      onChange={value =>
                        handleChange(
                          'elasticAnalysisTypes',
                          state.elasticAnalysisTypes.map(item => ({
                            ...item,
                            active: value.includes(item.value),
                          }))
                        )
                      }
                      options={state.elasticAnalysisTypes}
                    />
                  </Grid>
                </Grid>
                <Grid item xs={12} container spacing={2} alignItems="center">
                  <Grid item minWidth={200}>
                    <Typography
                      htmlFor="instanceSize"
                      variant="subtitle2"
                      component="label"
                    >
                      {t('forms.instance_size')}
                    </Typography>
                  </Grid>
                  <Grid item flexGrow={1}>
                    <Select
                      fullWidth
                      value={state.size}
                      onChange={value => handleChange('size', value)}
                      placeholder={t('forms.instance_size_placeholder')}
                      options={
                        awsInstanceSizes?.map(item => ({
                          name: item.name,
                          value: item.key,
                        })) || []
                      }
                    />
                  </Grid>
                </Grid>
              </>
            )}
          </Grid>
        </Paper>
      </SecondaryContentWrapper>
      <Grid item xs={12} textAlign="right" mt={2}>
        <Button
          variant="contained"
          color="success"
          startIcon={<Icon iconName="save" />}
          onClick={handleSave}
        >
          {t('buttons.save_configuration')}
        </Button>
      </Grid>
    </>
  );
};
